import React, { useState, useEffect, useMemo } from 'react';
import { Box, Grid, Typography, TextField, InputAdornment, IconButton, Skeleton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import IndexCard from './components/IndexCard';
import { fetchAllIndices } from '../../../../services/backendAPI/index';
import octopusInstance from '../../../../services/octopus/octopusInstance';

/**
 * AllIndicesTab: Fetches and displays a live list of all market indices.
 */
const AllIndicesTab = () => {
  const [indices, setIndices] = useState([]);
  const [liveData, setLiveData] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // 1. Fetch initial list of all indices
  useEffect(() => {
    const loadIndices = async () => {
      setLoading(true);
      try {
        // Calling API directly
        const res = await fetchAllIndices();
        const list = res.data || res.result || [];
        setIndices(list);
      } catch (err) {
        console.error("Failed to fetch all indices:", err);
      } finally {
        setLoading(false);
      }
    };
    loadIndices();
  }, []);

  // 2. WebSocket Subscriptions for live updates
  useEffect(() => {
    if (indices.length === 0) return;

    const handlers = indices.map(idx => {
      const handler = octopusInstance.wsHandler({
        messageType: 'CompactMarketDataMessage',
        subscriptionLocation: `all-indices-tab-${idx.token}`,
        payload: { 
          exchangeCode: idx.exchange_code || (idx.exchange === 'NSE' ? 1 : 2), 
          instrumentToken: idx.token 
        },
      });

      handler.subscribe(({ msg }) => {
        if (!msg) return;
        setLiveData(prev => ({
          ...prev,
          [idx.token]: {
            ltp:    msg.lastTradedPrice ?? msg.ltp ?? prev[idx.token]?.ltp ?? 0,
            change: msg.absoluteChange  ?? prev[idx.token]?.change ?? 0,
            pct:    msg.percentChange   ?? prev[idx.token]?.pct ?? 0,
          }
        }));
      });
      return handler;
    });

    return () => handlers.forEach(h => h && h.unsubscribe());
  }, [indices]);

  const filteredIndices = useMemo(() => {
    if (!searchQuery.trim()) return indices;
    const q = searchQuery.toLowerCase();
    return indices.filter(idx => 
      (idx.trading_symbol || idx.symbol || '').toLowerCase().includes(q)
    );
  }, [indices, searchQuery]);

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, lg: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: { xs: 1.5, sm: 0 } }}>
          <Typography variant="h6" fontWeight="800" sx={{ fontSize: { xs: '1rem', lg: '1.25rem' } }}>Market Indices</Typography>
          <Skeleton animation="wave" variant="rounded" width={280} height={40} />
        </Box>
        <Grid container spacing={2}>
          {Array.from({ length: 12 }).map((_, idx) => (
            <Grid item xs={12} sm={6} lg={4} xl={3} key={idx}>
              <Skeleton animation="wave" variant="rounded" height={100} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, lg: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: { xs: 1.5, sm: 0 } }}>
        <Typography variant="h6" fontWeight="800" sx={{ fontSize: { xs: '1rem', lg: '1.25rem' } }}>Market Indices</Typography>
        <TextField
          size="small"
          placeholder="Search Indices..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ width: { xs: '100%', sm: '280px' } }}
        />
      </Box>

      {filteredIndices.length > 0 ? (
        <Grid container spacing={2}>
          {filteredIndices.map((idx) => (
            <Grid item xs={12} sm={6} lg={4} xl={3} key={idx.token}>
              <IndexCard 
                data={{
                  ...idx,
                  ...liveData[idx.token]
                }} 
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography color="text.secondary">No indices found</Typography>
        </Box>
      )}
    </Box>
  );
};

export default AllIndicesTab;
